/**
 * What every role may do, shown to the person deciding who gets which.
 *
 * Generated from the same `can()` the server enforces, never from a written
 * list. A hand-maintained table on this screen would be wrong within a release
 * and wrong in the most expensive way: an administrator would grant a role on
 * the strength of it. Reading it from the matrix means it cannot say anything
 * the product does not actually do.
 */
import { Resource, ROLE_DESCRIPTIONS, ROLE_LABELS, UserRole, can } from '@mouldtrack/shared';
import { Dialog } from '../../components/ui/dialog.js';
import { Button } from '../../components/ui/button.js';
import { Badge } from '../../components/ui/primitives.js';

/** Plain names for the resources. "catalog-masters" means nothing to a manager. */
const RESOURCE_LABELS: Record<Resource, string> = {
  moulds: 'Mould catalog',
  'catalog-masters': 'Master data',
  locations: 'Locations',
  stores: 'Stores',
  checkpoints: 'Checkpoints',
  users: 'People',
  scans: 'Scanning',
  transfers: 'Transfers',
  journeys: 'Journeys',
  nameplates: 'Nameplates',
  reports: 'Reports',
  backups: 'Backups',
};

/** The order an administrator thinks in, not the order the type declares. */
const RESOURCE_ORDER: Resource[] = [
  'moulds',
  'scans',
  'checkpoints',
  'locations',
  'stores',
  'catalog-masters',
  'transfers',
  'journeys',
  'nameplates',
  'reports',
  'users',
  'backups',
];

/** The roles actually offered when adding someone, legacy ones last. */
const ROLE_ORDER: UserRole[] = [
  'ADMIN',
  'INVENTORY_MANAGER',
  'LOCATION_SUPERVISOR',
  'VIEWER',
  'STORE_KEEPER',
  'GATE_SECURITY',
];

/**
 * One cell, as a sentence rather than five ticks.
 *
 * "View, add, edit" is read at a glance; a row of checkboxes under Create /
 * Update / Delete / Export headings has to be decoded, and this table is forty
 * cells before anyone starts.
 */
function summarise(role: UserRole, resource: Resource): { text: string; tone: 'none' | 'read' | 'write' | 'full' } {
  const may = (a: Parameters<typeof can>[1]) => can(role, a, resource);
  if (!may('read')) return { text: 'No access', tone: 'none' };

  const verbs: string[] = ['View'];
  if (may('create')) verbs.push('add');
  if (may('update')) verbs.push('edit');
  if (may('delete')) verbs.push('delete');
  if (may('export')) verbs.push('export');

  const tone = may('delete') ? 'full' : may('create') || may('update') ? 'write' : 'read';
  return { text: verbs.join(', '), tone };
}

export function RolePermissionsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="What each role can do"
      description="Set on the server as well as here - a role cannot do more than this by using a different screen."
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-4 pb-2">
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-sunken/60">
                <th className="sticky left-0 z-10 bg-surface-sunken px-3 py-2 text-left text-xs font-semibold text-muted-foreground">
                  Area
                </th>
                {ROLE_ORDER.map((r) => (
                  <th
                    key={r}
                    className="min-w-32 px-3 py-2 text-left text-xs font-semibold text-muted-foreground"
                  >
                    {ROLE_LABELS[r]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {RESOURCE_ORDER.map((resource) => (
                <tr key={resource}>
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-surface px-3 py-2 text-left font-medium"
                  >
                    {RESOURCE_LABELS[resource]}
                  </th>
                  {ROLE_ORDER.map((role) => {
                    const { text, tone } = summarise(role, resource);
                    return (
                      <td key={role} className="px-3 py-2 align-middle">
                        {tone === 'none' ? (
                          <span className="text-subtle-foreground">-</span>
                        ) : (
                          <span
                            className={
                              tone === 'full'
                                ? 'text-foreground'
                                : tone === 'write'
                                  ? 'text-foreground'
                                  : 'text-muted-foreground'
                            }
                          >
                            {text}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-2 rounded-xl border border-border bg-surface-sunken/40 p-3">
          {ROLE_ORDER.map((r) => (
            <p key={r} className="text-sm">
              <b>{ROLE_LABELS[r]}</b>{' '}
              <span className="text-muted-foreground">{ROLE_DESCRIPTIONS[r]}</span>
            </p>
          ))}
        </div>

        {/* The one rule that is not in the matrix, because it is narrower than a
            resource: it applies per row, and getting it wrong is what lets a
            gate operator record a movement that never happened. */}
        <p className="flex items-start gap-2 rounded-xl border border-border bg-surface-sunken/40 p-3 text-sm text-muted-foreground">
          <Badge tone="primary" className="mt-0.5 shrink-0">
            Also
          </Badge>
          <span>
            Gate Security and Location Supervisor can be limited to named checkpoints. Edit the
            person and tick the gates they work - the scanner then offers only those, and the
            server refuses a scan taken anywhere else. Ticking nothing means no restriction.
          </span>
        </p>
      </div>
    </Dialog>
  );
}
